import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import { absentModalState, pharmacyState } from '@/recoil/pharmacy';
import { notificationState } from '@/recoil/notification';
import { authState } from '@/recoil/auth';

import NotificationModal from './containers/NotificationModal';
import Header from './containers/Header';
import usePharmacyQuery from '@/hooks/pharmacy/usePharmacyQuery';
import Navigation from './containers/Navigation';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AbsentModal from '../AbsentModal';
import pharmacyServices from '@/services/phamacy';
import RightSideBanner from '@/components/Advertisement/RightSideBanner';
import useDayOfWeekQuery from '@/hooks/common/useDayOfWeekQuery';
import moment from 'moment';

const AppLayout: React.FC = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const auth = useRecoilValue(authState);
  const pharmacy = useRecoilValue(pharmacyState);
  const [absentModal, setAbsentModal] = useRecoilState(absentModalState);
  const [notification, setNotification] = useRecoilState(notificationState);
  const isLoggedIn = !isEmpty(pharmacy) && !!auth.token;
  const goMain = () => history.push('/');
  const {
    requestBePharmacyPresentMutation,
    requestBePharmacyAbsentMutation
  } = usePharmacyQuery();
  const { isAbsent } = pharmacyServices.getPharmacyIsAbsentService();
  const todayOfWeek = useDayOfWeekQuery(moment().format('YYYY-MM-DD'));

  const path = window.location.pathname;
  const isTermAgreePage: boolean = path.includes('termAgree');
  const navInit = isLoggedIn && !isTermAgreePage;
  const [navVisible, setNavVisible] = useState(navInit);

  const isOnLoginPage = location.pathname === '/login';
  const displayTodayDuty = pharmacyServices.buildDisplayTodayDuty(pharmacy, todayOfWeek);

  const handleCloseModal = () => {
    setAbsentModal(false);
  };

  const handleClickBePresent = async () => {
    await requestBePharmacyPresentMutation.mutateAsync();
    handleCloseModal();
  };

  const handleClickBeAbsent = async () => {
    await requestBePharmacyAbsentMutation.mutateAsync();
  };

  window.onpopstate = function () {
    const path = window.location.pathname;
    const isTermAgreePage: boolean = path.includes('termAgree');
    if (isLoggedIn && !isTermAgreePage) {
      setNavVisible(true);
    }
  };

  useEffect(() => {
    if (isTermAgreePage) {
      setNavVisible(false);
      return;
    }

    setNavVisible(isLoggedIn);
  }, [isLoggedIn, isTermAgreePage, location]);

  return (
    <Wrapper>
      <Header
        isLoggedIn={isLoggedIn}
        goMain={goMain}
        isAbsent={isAbsent}
        onClickBeAbsent={handleClickBeAbsent}
        onClickBePresent={handleClickBePresent}
      />
      <Body>
        {navVisible && (
          <Navigation
            isLoggedIn={isLoggedIn}
            displayTodayDuty={displayTodayDuty}
            goMain={goMain}
            pharmacy={pharmacy}
            notification={notification}
            setNotification={setNotification}
          />
        )}
        {!isOnLoginPage ? (
          <Main nav={navVisible}>
            <ContentContainer>
              <div>
                {children}
                <RightSideBanner />
              </div>
            </ContentContainer>
          </Main>
        ) : (
          <LoginContainer>{children}</LoginContainer>
        )}
      </Body>
      {!!notification.recent && (
        <NotificationModal
          notification={notification}
          setNotification={setNotification}
        />
      )}
      {absentModal && (
        <AbsentModal
          onClickBePresent={handleClickBePresent}
          onClose={handleCloseModal}
        />
      )}
      <Toast limit={3} />
    </Wrapper>
  );
};

export default AppLayout;

const Wrapper = styled.div``;

const Body = styled.div`
  display: flex;
  height: 100%;
`;

const Main = styled.div<{ nav: boolean }>`
  min-height: calc(100vh - 80px);
  max-height: 100%;
  margin-top: 80px;
  padding: 32px 0 32px 32px;
  box-sizing: border-box;
`;

const Toast = styled(ToastContainer)`
  --toastify-toast-width: 360px;

  .Toastify__toast {
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 8px;
    padding: 16px 16px 16px 20px;
  }

  .Toastify__toast-body {
    padding: 0;
  }

  .Toastify__progress-bar--info {
    background-color: rgba(0, 0, 0, 0.8);
  }

  .Toastify__toast-icon {
    width: 40px;
  }

  .Toastify__close-button {
    color: white;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;

  & > div:first-of-type {
    width: 1200px;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const LoginContainer = styled.div`
  width: 100%;
  height: calc(100vh - 80px);
`;
